// Koppeling pro forma <-> definitieve factuur.
//
// De koppeling staat op twee plekken: `linkedFinalInvoiceId` op de pro forma (één) en
// `linkedProformaInvoiceIds` op de definitieve factuur (lijst). Schermen lezen niet allemaal
// dezelfde kant, dus de twee moeten altijd samen en gecontroleerd geschreven worden. Deze
// functie bepaalt wát er geschreven moet worden; de aanroeper doet dat in één transactie,
// met documenten die binnen die transactie zijn gelezen.
//
// Gespiegeld in supplier-portal/lib/proformaLinks.ts. Wijzig je hier iets, wijzig het daar ook.

const sameIds = (a, b) => a.length === b.length && a.every((id) => b.includes(id));

/**
 * @param {object} params
 * @param {object} params.finalInvoice  { id, invoiceNumber, supplierId, currency, linkedProformaInvoiceIds }
 * @param {string[]} params.desiredIds  de pro forma's die na het opslaan aan deze factuur moeten hangen
 * @param {Map<string, object>|object} params.proformasById  verse documenten van alle pro forma's in
 *   (huidige lijst ∪ desiredIds); een ontbrekend document betekent "bestaat niet"
 * @param {object[]} params.otherFinals  andere definitieve facturen die een van deze pro forma's in
 *   hun lijst hebben (vers gelezen)
 * @returns {{
 *   conflicts: {proformaId: string, invoiceNumber: string, reason: string, otherInvoiceId?: string}[],
 *   linkedIds: string[], added: string[], removed: string[], restored: string[],
 *   proformaUpdates: {id: string, linkedFinalInvoiceId: string|null}[],
 *   releasedFrom: {id: string, invoiceNumber: string, linkedProformaInvoiceIds: string[], releasedIds: string[]}[],
 *   changed: boolean,
 * }}
 */
export const planProformaLinkChange = ({ finalInvoice, desiredIds, proformasById, otherFinals = [] }) => {
  const get = (id) => (proformasById instanceof Map ? proformasById.get(id) : proformasById[id]);
  const current = finalInvoice.linkedProformaInvoiceIds || [];
  const desired = [...new Set(desiredIds || [])];

  const added = desired.filter((id) => !current.includes(id));
  const removed = current.filter((id) => !desired.includes(id));
  const kept = desired.filter((id) => current.includes(id));

  const conflicts = [];
  const proformaUpdates = [];
  const restored = [];
  const numberOf = (id) => get(id)?.invoiceNumber || id;

  const checkLinkable = (id, isNew) => {
    const p = get(id);
    if (!p) {
      conflicts.push({ proformaId: id, invoiceNumber: id, reason: "not_found" });
      return false;
    }
    if (p.type !== "proforma") {
      conflicts.push({ proformaId: id, invoiceNumber: numberOf(id), reason: "not_proforma" });
      return false;
    }
    if (p.supplierId !== finalInvoice.supplierId) {
      conflicts.push({ proformaId: id, invoiceNumber: numberOf(id), reason: "other_supplier" });
      return false;
    }
    // Alleen bij nieuwe koppelingen: een bestaande koppeling met afwijkende valuta (van vóór
    // deze controle) mag een andere wijziging op deze factuur niet blokkeren.
    if (isNew && p.currency && finalInvoice.currency && p.currency !== finalInvoice.currency) {
      conflicts.push({ proformaId: id, invoiceNumber: numberOf(id), reason: "currency" });
      return false;
    }
    if (p.linkedFinalInvoiceId && p.linkedFinalInvoiceId !== finalInvoice.id) {
      conflicts.push({
        proformaId: id,
        invoiceNumber: numberOf(id),
        reason: "linked_elsewhere",
        otherInvoiceId: p.linkedFinalInvoiceId,
      });
      return false;
    }
    return true;
  };

  added.forEach((id) => {
    if (checkLinkable(id, true)) proformaUpdates.push({ id, linkedFinalInvoiceId: finalInvoice.id });
  });
  // Een pro forma die al in de lijst stond maar zelf nergens naar wijst (een halve koppeling),
  // krijgt zijn terugverwijzing alsnog. Wijst hij naar een andere factuur, dan is dat een conflict
  // dat de gebruiker moet oplossen door hem hier of daar los te halen.
  kept.forEach((id) => {
    if (checkLinkable(id, false) && !get(id).linkedFinalInvoiceId) {
      proformaUpdates.push({ id, linkedFinalInvoiceId: finalInvoice.id });
      restored.push(id);
    }
  });
  // Ontkoppelen wist de terugverwijzing alleen als die nog naar déze factuur wijst. Wijst hij
  // ergens anders naar, dan hoort de pro forma daar en blijft hij onaangeroerd.
  removed.forEach((id) => {
    const p = get(id);
    if (p && p.linkedFinalInvoiceId === finalInvoice.id) proformaUpdates.push({ id, linkedFinalInvoiceId: null });
  });

  // Andere definitieve facturen die een pro forma claimen die vanaf nu (aantoonbaar) bij deze
  // factuur hoort: hun claim is achterhaald, want de pro forma wees niet naar hen (anders was het
  // hierboven een conflict geweest). Haal hem daar weg, anders telt hij bij beide mee.
  const claimedHere = [...added, ...restored].filter((id) => proformaUpdates.some((u) => u.id === id));
  const releasedFrom = otherFinals
    .filter((f) => f.id !== finalInvoice.id)
    .map((f) => {
      const list = f.linkedProformaInvoiceIds || [];
      const releasedIds = list.filter((id) => claimedHere.includes(id));
      return {
        id: f.id,
        invoiceNumber: f.invoiceNumber || f.id,
        linkedProformaInvoiceIds: list.filter((id) => !releasedIds.includes(id)),
        releasedIds,
      };
    })
    .filter((f) => f.releasedIds.length > 0);

  return {
    conflicts,
    linkedIds: desired,
    added,
    removed,
    restored,
    proformaUpdates,
    releasedFrom,
    changed: !sameIds(current, desired) || restored.length > 0 || releasedFrom.length > 0,
  };
};

// Leesbare melding voor een conflict, voor de foutmelding na het opslaan.
export const describeProformaLinkConflict = (conflict, invoicesById) => {
  const other = conflict.otherInvoiceId
    ? invoicesById?.get?.(conflict.otherInvoiceId)?.invoiceNumber || conflict.otherInvoiceId
    : null;
  switch (conflict.reason) {
    case "linked_elsewhere":
      return `${conflict.invoiceNumber} is al verrekend met definitieve factuur ${other}. Ontkoppel hem daar eerst.`;
    case "not_found":
      return `Pro forma ${conflict.invoiceNumber} bestaat niet meer.`;
    case "not_proforma":
      return `${conflict.invoiceNumber} is geen pro forma.`;
    case "other_supplier":
      return `${conflict.invoiceNumber} is van een andere leverancier.`;
    case "currency":
      return `${conflict.invoiceNumber} heeft een andere valuta dan deze factuur.`;
    default:
      return `${conflict.invoiceNumber}: koppelen niet mogelijk.`;
  }
};

/**
 * Controleert of de twee kanten van de koppeling overal met elkaar kloppen.
 * @param {object[]} invoices  alle facturen (pro forma's en definitieve)
 * @returns {Map<string, string[]>}  factuur-id -> meldingen, alleen voor facturen met een probleem
 */
export const findProformaLinkIssues = (invoices) => {
  const byId = new Map(invoices.map((inv) => [inv.id, inv]));
  const issues = new Map();
  const add = (id, text) => issues.set(id, [...(issues.get(id) || []), text]);
  const numberOf = (id) => byId.get(id)?.invoiceNumber || id;

  const claimsByProforma = new Map();
  invoices
    .filter((inv) => inv.type === "final")
    .forEach((f) =>
      (f.linkedProformaInvoiceIds || []).forEach((pid) =>
        claimsByProforma.set(pid, [...(claimsByProforma.get(pid) || []), f.id]),
      ),
    );

  claimsByProforma.forEach((finalIds, pid) => {
    const p = byId.get(pid);
    finalIds.forEach((fid) => {
      if (!p) add(fid, `Gekoppelde pro forma ${pid} bestaat niet meer`);
      else if (p.linkedFinalInvoiceId !== fid) {
        add(
          fid,
          p.linkedFinalInvoiceId
            ? `Pro forma ${numberOf(pid)} staat in de lijst, maar is verrekend met ${numberOf(p.linkedFinalInvoiceId)} - telt hier ten onrechte mee`
            : `Pro forma ${numberOf(pid)} staat in de lijst, maar de pro forma zelf staat op "niet verrekend"`,
        );
      }
    });
    if (finalIds.length > 1) {
      add(pid, `Wordt geclaimd door meerdere definitieve facturen: ${finalIds.map(numberOf).join(", ")}`);
    }
    if (p && !p.linkedFinalInvoiceId) {
      add(pid, `Staat op "niet verrekend", maar ${finalIds.map(numberOf).join(", ")} rekent hem wel mee`);
    }
  });

  invoices
    .filter((inv) => inv.type === "proforma" && inv.linkedFinalInvoiceId)
    .forEach((p) => {
      const f = byId.get(p.linkedFinalInvoiceId);
      if (!f) add(p.id, `Verrekend met een definitieve factuur die niet meer bestaat (${p.linkedFinalInvoiceId})`);
      else if (!(f.linkedProformaInvoiceIds || []).includes(p.id)) {
        add(p.id, `Verrekend met ${numberOf(f.id)}, maar die factuur rekent hem niet mee`);
      }
    });

  return issues;
};

/**
 * Betaald/openstaand/status van een definitieve factuur: eigen betalingen plus wat er op de
 * gekoppelde pro forma's betaald is. Eén formule voor koppelen, verwijderen en betalingen.
 * Status altijd vers afleiden: een opgeslagen status overnemen zou een verouderde waarde herhalen
 * (bijv. nog "partially_paid" na het ontkoppelen van de pro forma die dat veroorzaakte).
 */
export const computeFinalInvoiceTotals = ({ totalAmount = 0, ownPaid = 0, linkedPaid = 0 }) => {
  const totalPaid = Math.round((ownPaid + linkedPaid) * 100) / 100;
  const amountDue = Math.round(((totalAmount || 0) - totalPaid) * 100) / 100;
  const status = amountDue <= 0 && totalAmount > 0 ? "paid" : totalPaid > 0 ? "partially_paid" : "submitted";
  return { totalPaid, amountDue, status };
};

// Som van totalPaid over de pro forma's met deze ids (ontbrekende documenten tellen als 0).
export const sumProformaPaid = (ids, proformasById) =>
  (ids || []).reduce((sum, id) => sum + ((proformasById.get(id) || {}).totalPaid || 0), 0);

// Wijken de opgeslagen totalen af van de berekende?
export const totalsDiffer = (invoice, totals) =>
  Math.abs((invoice.totalPaid || 0) - totals.totalPaid) >= 0.005 ||
  Math.abs((invoice.amountDue ?? invoice.totalAmount ?? 0) - totals.amountDue) >= 0.005 ||
  (invoice.status || "submitted") !== totals.status;
